'use client';

import type { FC } from 'react';
import type { GameDto } from '@/queries/games/games.schema';
import { useQueries } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { RatingStars } from '@/components/shared/RatingStars';
import { AppImage } from '@/components/ui/AppImage/AppImage';
import { gameQueryOptions } from '@/queries/games/games.queries';
import { cn } from '@/utils/cn';
import { compareRows, getCellValue, getCompareGameAliases, hasComparableDetails } from './game-compare.utils';

interface GameCompareProps {
  game?: GameDto;
}

export const GameCompare: FC<GameCompareProps> = ({ game }) => {
  const t = useTranslations('game');

  // TODO: Since we need to take games from the top games category, but right now we don't have all the games with content, so a different logic is implemented
  // it's should be used prefetch logic instead of this
  // const { data: topGamesData } = useQuery({
  //   ...gameQueryOptions.list({
  //     page: 1,
  //     limit: 50,
  //     sort: {
  //       order: 'desc',
  //     },
  //     filters: {
  //       categoryId: TOP_GAMES_CATEGORY_ID,
  //     },
  //   }),
  //   enabled: Boolean(game?.id),
  // });

  const compareAliases = useMemo(() => getCompareGameAliases(game), [game]);

  const compareGamesQueries = useQueries({
    queries: compareAliases.map(alias => ({
      ...gameQueryOptions.one(alias),
      enabled: Boolean(game?.id),
    })),
  });

  const gamesForCompare = (() => {
    if (!game?.id || compareGamesQueries.length < 1) {
      return [];
    }

    const candidates = compareGamesQueries
      .map(query => query.data)
      .filter((item): item is GameDto => Boolean(item))
      .filter(item => item.id !== game.id)
      .filter(item => hasComparableDetails(item.details));

    const uniqueCandidates = Array.from(new Map(candidates.map(item => [item.id, item])).values());

    return uniqueCandidates.slice(0, 3);
  })();

  if (!game || !hasComparableDetails(game.details) || gamesForCompare.length < 1) {
    return null;
  }

  const comparedGames = [game, ...gamesForCompare];

  return (
    <section id="game-compare" className="max-w-200">
      <h3 className="text-xl leading-[1.3] font-medium tracking-normal text-light">{t('game-details.compare-game', { name: game.name || '' })}</h3>

      <div className="mt-5 -ml-4 -mr-4 lg:-ml-10 lg:-mr-10 xl:mx-0 overflow-x-auto hide-scrollbar">
        <div className="flex w-max min-w-2xl pl-4 lg:pl-10 xl:px-0 [--compare-label-col:6.875rem] md:[--compare-label-col:11.875rem]">
          <div className="flex-none">
            <div
              className="grid"
              style={{
                gridTemplateColumns: `var(--compare-label-col) repeat(${comparedGames.length}, 9rem)`,
              }}
            >
              <div />
              {comparedGames.map(compareGame => (

                <div className="ml-5" key={compareGame.id}>
                  <div className="w-30 rounded-xl overflow-hidden aspect-3/4 bg-c800">
                    {compareGame.cover && (
                      <AppImage
                        src={compareGame.cover?.key ?? compareGame.cover?.path}
                        alt={compareGame.cover?.meta?.alt || compareGame.name}
                        variant="game-cover-grid"
                        className="h-full w-full"
                      />
                    )}
                  </div>
                  <p className="mt-2 text-light text-sm leading-[1.2] font-medium truncate">{compareGame.name}</p>
                </div>
              ))}
            </div>

            <div className="mt-2 md:mt-3 relative w-170 md:w-200">
              <div
                className="pointer-events-none absolute top-0 bottom-0 w-px bg-violet-dark"
                style={{ left: 'var(--compare-label-col)' }}
              />
              {compareRows.map((row, index) => (
                <div
                  key={`compare-row-${row.key}`}
                  className="relative grid gap-x-4.5"
                  style={{
                    gridTemplateColumns: `var(--compare-label-col) repeat(${comparedGames.length}, 7.875rem)`,
                  }}
                >
                  <p className="text-c300 text-sm font-medium py-2.5 truncate">{t(row.labelKey)}</p>
                  {comparedGames.map(compareGame => (
                    <div
                      key={`${compareGame.id}-${row.key}`}
                      className={cn('py-2.5 shrink-0 text-c300 text-sm truncate', index === 0 && 'pt-3.5')}
                    >
                      {
                        row.key === 'rating'
                          ? <RatingStars rating={5} maxRating={5} showValue size="sm" interactive={false} />
                          : getCellValue(compareGame, row.key)
                      }
                    </div>
                  ))}
                  {
                    index < compareRows.length - 1 && (
                      <div className="pointer-events-none absolute right-0 bottom-0 left-0 h-px bg-violet-dark" />
                    )
                  }
                </div>
              ))}
            </div>
          </div>
          <div className="w-4 lg:w-10 xl:w-0 shrink-0" aria-hidden />
        </div>
      </div>
    </section>
  );
};
